import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import * as React from 'react';

export interface Column<T = any> {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: any, row: T) => React.ReactNode;
  renderCell?: (row: T) => React.ReactNode;
}

interface StickyHeadTableProps<T = any> {
  columns: readonly Column<T>[];
  rows: T[];
  getRowId: (row: T) => string | number;
  pagination?: {
    totalData: number;
    totalPages: number,
    currentPage: number;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
  };
}

export default function StickyHeadTable<T = any>({
  columns,
  rows,
  getRowId,
  pagination
}: StickyHeadTableProps<T>) {

  const handlePreviousPage = () => {
    pagination?.onPageChange(pagination.currentPage - 1);
  };

  const handleNextPage = () => {
    pagination?.onPageChange(pagination.currentPage + 1);
  };

  return (
    <Paper variant="outlined" sx={{ width: '100%', overflow: 'hidden', boxShadow: '0 8px 24px -14px rgba(15,23,42,0.12)' }}>
      <TableContainer sx={{ maxHeight: 620 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .map((row) => {
                return (
                  <TableRow hover tabIndex={-1} key={getRowId(row)}>
                    {columns.map((column) => {
                      return (
                        <TableCell key={column.id} align={column.align || 'left'}>
                          {column.renderCell
                            ? column.renderCell(row)
                            : column.format
                              ? column.format((row as any)[column.id], row)
                              : (row as any)[column.id]}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                  Sem registos para mostrar
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Total: {pagination.totalData} itens
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handlePreviousPage}
              disabled={pagination.currentPage === 1}
            >
              Anterior
            </Button>
            <Typography variant="body2">
              Página {pagination.currentPage} de {pagination.totalPages}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleNextPage}
              disabled={pagination.currentPage >= pagination.totalPages}
            >
              Próxima
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
